import { ApiError, type ApiErrorProblem } from './ApiError';
import { apiConfig } from './apiConfig';
import { AuthToken } from './authToken';
import { recordDuration, recordSample } from '@/shared/utils/perf';
import { parseContentDispositionFilename } from '@/shared/utils/download';

export type QueryParams = Record<string, string | number | boolean | undefined>;

export interface RequestOptions {
  skipAuth?: boolean;
  signal?: AbortSignal;
}

/** Authenticated binary download result (CSV exports). ADR-003 §8. */
export interface BlobResponse {
  blob: Blob;
  filename: string | null;
}

const VIDEO_LIST_PATH = 'media/video';
let didSampleVideoFetchTransferSize = false;

function buildQueryString(params?: QueryParams): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined
  );
  if (entries.length === 0) return '';
  const queryString = entries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');
  return `?${queryString}`;
}

function buildUrl(path: string, params?: QueryParams): string {
  const { baseUrl, version } = apiConfig;
  const queryString = buildQueryString(params);
  return `${baseUrl}/v${version}/${path}${queryString}`;
}

function shouldMeasureVideosFetch(method: string, path: string): boolean {
  return method === 'GET' && path === VIDEO_LIST_PATH;
}

function recordVideoTransferSizeSample(url: string): void {
  if (didSampleVideoFetchTransferSize) return;
  if (typeof performance === 'undefined') return;

  queueMicrotask(() => {
    if (didSampleVideoFetchTransferSize) return;

    const entries = performance.getEntriesByName(url, 'resource');
    if (entries.length === 0) return;

    const latest = entries[entries.length - 1];
    if (!(latest instanceof PerformanceResourceTiming)) return;
    if (latest.transferSize <= 0) return;

    didSampleVideoFetchTransferSize = true;
    recordSample('videos.fetch.transferSize', latest.transferSize, {
      encodedBodySize: latest.encodedBodySize,
      decodedBodySize: latest.decodedBodySize,
      debugOnce: true,
    });
  });
}

function getHeaders(authToken: string | null): HeadersInit {
  const headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  if (authToken) {
    headers.Authorization = `Basic ${btoa(`${authToken}:`)}`;
  }
  return headers;
}

async function parseResponse<T>(response: Response, url: string): Promise<T> {
  const contentLength = response.headers.get('content-length');
  if (contentLength === '0') {
    return {} as T;
  }

  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
  if (contentType.includes('application/json')) {
    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw new ApiError({
        httpStatusCode: response.status,
        message: 'Invalid JSON response',
        url,
      });
    }
    return checkedBody<T>(json, response.status, url);
  }

  const text = await response.text();
  if (!text || text === 'OK') {
    return {} as T;
  }

  try {
    return checkedBody<T>(JSON.parse(text), response.status, url);
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError({
      httpStatusCode: response.status,
      message: `Invalid JSON response: ${text.substring(0, 100)}`,
      url,
    });
  }
}

/**
 * The /v1 catch-all completes uncaught server Throwables as HTTP 200 with a
 * VError body — surface those as errors instead of handing an error envelope
 * to callers typed for the happy path (see ApiError.fromErrorEnvelope).
 */
function checkedBody<T>(json: unknown, wireStatus: number, url: string): T {
  const envelopeError = ApiError.fromErrorEnvelope(json, wireStatus, url);
  if (envelopeError) throw envelopeError;
  return json as T;
}

async function buildApiError(response: Response, url: string): Promise<ApiError> {
  const text = await response.text();
  let message = response.statusText;
  let data: Record<string, string> | undefined;
  let code: string | undefined;
  let problems: ApiErrorProblem[] | undefined;
  let availableFrom: number | undefined;
  let availableTo: number | undefined;
  let bodyStatusCode: number | undefined;

  try {
    const json = JSON.parse(text);
    // Platform VError public envelope: {httpStatusCode, msg, data}
    if (typeof json.httpStatusCode === 'number') bodyStatusCode = json.httpStatusCode;
    if (json.msg) message = json.msg;
    if (json.safeMsg) message = json.safeMsg;
    if (json.message) message = json.message;
    if (json.data) data = json.data;
    // App factory envelope (relayed verbatim): {code, message, problems?:[{field, message}]}
    if (typeof json.code === 'string') code = json.code;
    if (Array.isArray(json.problems)) {
      problems = json.problems.filter(
        (p: unknown): p is ApiErrorProblem =>
          typeof p === 'object' && p !== null &&
          typeof (p as ApiErrorProblem).field === 'string' &&
          typeof (p as ApiErrorProblem).message === 'string',
      );
    }
    // Analytics structured error envelope: {error:{code,message,availableFrom?,availableTo?}}
    if (json.error && typeof json.error === 'object') {
      if (typeof json.error.code === 'string') code = json.error.code;
      if (typeof json.error.message === 'string') message = json.error.message;
      if (typeof json.error.availableFrom === 'number') availableFrom = json.error.availableFrom;
      if (typeof json.error.availableTo === 'number') availableTo = json.error.availableTo;
    }
  } catch {
    if (text) message = text;
  }

  return new ApiError({
    httpStatusCode: response.status,
    message,
    data,
    url,
    code,
    problems,
    availableFrom,
    availableTo,
    bodyStatusCode,
  });
}

class WebApi {
  private async request<T>(
    method: string,
    path: string,
    params?: QueryParams,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    const url = buildUrl(path, params);
    const shouldMeasure = shouldMeasureVideosFetch(method, path);
    const requestStart = shouldMeasure && typeof performance !== 'undefined'
      ? performance.now()
      : 0;
    const authToken = options?.skipAuth ? null : AuthToken.get();
    const headers = getHeaders(authToken);

    const init: RequestInit = {
      method,
      headers,
      mode: 'cors',
    };

    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }

    if (options?.signal) {
      init.signal = options.signal;
    }

    const response = await fetch(url, init);
    if (shouldMeasure) {
      recordVideoTransferSizeSample(url);
    }

    if (!response.ok) {
      if (shouldMeasure && requestStart > 0) {
        recordDuration('videos.fetch.total', performance.now() - requestStart, {
          ok: false,
          status: response.status,
        });
      }
      if (response.status === 401) {
        AuthToken.handleUnauthorized();
        throw new ApiError({
          httpStatusCode: response.status,
          message: 'Unauthorized',
          url,
        });
      }
      const apiError = await buildApiError(response, url);
      // Expired/invalid sessions never arrive as wire 401 — only as the
      // 403-with-body-401 rejection signature on authenticated requests.
      if (authToken !== null && apiError.isNotAuthenticated) {
        AuthToken.handleUnauthorized();
      }
      throw apiError;
    }

    const parseStart = shouldMeasure && typeof performance !== 'undefined'
      ? performance.now()
      : 0;

    try {
      const parsed = await parseResponse<T>(response, url);

      if (shouldMeasure && parseStart > 0 && requestStart > 0) {
        recordDuration('videos.fetch.jsonParse', performance.now() - parseStart, {
          status: response.status,
          ok: true,
        });
        recordDuration('videos.fetch.total', performance.now() - requestStart, {
          ok: true,
          status: response.status,
        });
      }

      return parsed;
    } catch (error) {
      if (shouldMeasure && requestStart > 0) {
        recordDuration('videos.fetch.total', performance.now() - requestStart, {
          ok: false,
          status: response.status,
          parseError: true,
        });
      }
      throw error;
    }
  }

  async get<T>(path: string, params?: QueryParams, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, params, undefined, options);
  }

  async post<T>(
    path: string,
    params?: QueryParams,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('POST', path, params, body, options);
  }

  async put<T>(
    path: string,
    params?: QueryParams,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('PUT', path, params, body, options);
  }

  async delete<T>(path: string, params?: QueryParams, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, params, undefined, options);
  }

  private async requestBlob(
    method: string,
    path: string,
    params?: QueryParams,
    body?: unknown,
    options?: RequestOptions
  ): Promise<BlobResponse> {
    const url = buildUrl(path, params);
    const authToken = options?.skipAuth ? null : AuthToken.get();

    const headers: HeadersInit = { Accept: 'text/csv, */*' };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (authToken) {
      headers.Authorization = `Basic ${btoa(`${authToken}:`)}`;
    }

    const init: RequestInit = { method, headers, mode: 'cors' };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }
    if (options?.signal) {
      init.signal = options.signal;
    }

    const response = await fetch(url, init);

    if (!response.ok) {
      if (response.status === 401) {
        AuthToken.handleUnauthorized();
        throw new ApiError({
          httpStatusCode: response.status,
          message: 'Unauthorized',
          url,
        });
      }
      // Errors arrive as JSON before any CSV byte — route through shared parsing.
      const apiError = await buildApiError(response, url);
      if (authToken !== null && apiError.isNotAuthenticated) {
        AuthToken.handleUnauthorized();
      }
      throw apiError;
    }

    const blob = await response.blob();
    const filename = parseContentDispositionFilename(
      response.headers.get('content-disposition')
    );
    return { blob, filename };
  }

  /** Authenticated blob GET (CSV exports). Errors arrive as JSON and throw ApiError. */
  async getBlob(path: string, params?: QueryParams, options?: RequestOptions): Promise<BlobResponse> {
    return this.requestBlob('GET', path, params, undefined, options);
  }

  /** Authenticated blob POST with a JSON body (legacy report CSV exports). */
  async postBlob(
    path: string,
    params?: QueryParams,
    body?: unknown,
    options?: RequestOptions
  ): Promise<BlobResponse> {
    return this.requestBlob('POST', path, params, body, options);
  }

  async doLogin<T>(path: string, username: string, password: string): Promise<T> {
    const url = buildUrl(path);
    const headers: HeadersInit = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Basic ${btoa(`${username}:${password}`)}`,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers,
      mode: 'cors',
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      // No handleUnauthorized here: bad credentials share the not-authenticated
      // signature and must surface on the login form, not redirect.
      throw await buildApiError(response, url);
    }

    return parseResponse<T>(response, url);
  }
}

export const webApi = new WebApi();
